import type { ChipLedger, GameweekResult, PlayerProfileBundle } from './types'
import type { AvatarPresetId } from './avatarPresets'
import { computeSeasonStats, computeFormHistory } from './season'
import { mockResults } from './mockResults'
import { mockFriends } from './mockFriends'
import { initialsFromName } from './textUtils'
import { findTeamId, teamAbbr, type TeamId } from './teams'

/**
 * Builds another player's profile bundle. Identity (name, favourite team, avatar) comes from
 * the live server fetch; the STATS (total points, intuition rating, form) are computed from
 * their real, scored `season_history` — the same source the signed-in user's own stats use —
 * so every player's progress follows the dev-tool simulation: empty until they've been
 * simulated, and zeroed by a reset. `history` is empty when the live fetch hasn't resolved yet
 * or the player has no rows.
 *
 * Favourite team and `chipsUsed` have no fallback at all: absent means absent, so the profile
 * renders a crest-less header and no chip tiles. Anything else prints someone else's club or the
 * viewer's own chips under this player's name, silently and plausibly.
 */
export function getOtherPlayerProfile(
  userId: string,
  displayName: string,
  live?: {
    favouriteTeam?: TeamId | string | null
    avatarPreset?: AvatarPresetId | null
    seasonHistory?: GameweekResult[]
    chipsUsed?: ChipLedger | null
  }
): PlayerProfileBundle {
  const rosterEntry = mockFriends.find((f) => f.userId === userId)
  // Their live value or nothing. A club is a statement about a person, so an absent or
  // unrecognised one renders a crest-less header — never a stand-in. There used to be two
  // fallbacks here, and the last was a hash of the user id over the three clubs in `mockResults`,
  // so a third of all visited profiles claimed the viewed player supported Liverpool.
  const favouriteTeam: TeamId | null = findTeamId(live?.favouriteTeam)

  const history = live?.seasonHistory ?? []
  const trimmedName = (rosterEntry?.name ?? displayName).trim()
  const name = trimmedName || 'Unknown player'

  return {
    profile: {
      name,
      initials: trimmedName ? initialsFromName(trimmedName) : '??',
      favouriteTeam,
      favouriteTeamAbbr: favouriteTeam ? teamAbbr(favouriteTeam) : '',
      avatarPreset: live?.avatarPreset ?? null,
    },
    overallScore: history.reduce((sum, gw) => sum + gw.points, 0),
    seasonStats: computeSeasonStats(history),
    formHistory: computeFormHistory(history),
    chipsUsed: live?.chipsUsed ?? null,
    recentResults: (favouriteTeam ? mockResults[favouriteTeam] : undefined) ?? [],
  }
}
